import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, KeyRound, UserPlus, Power, PowerOff, Pencil, Users } from "lucide-react";
import api from "../../lib/api";
import { Spinner } from "../Bits";

export default function AdminAccountsPanel({ goToUsers }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [pwEditingId, setPwEditingId] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [nameEditingId, setNameEditingId] = useState(null);
  const [newName, setNewName] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meR, listR] = await Promise.all([
        api.get("/admin/auth/me").catch(() => ({ data: null })),
        api.get("/admin/auth/list").catch(() => ({ data: [] })),
      ]);
      setMe(meR.data);
      setAdmins(listR.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/auth/create", { email: form.email, name: form.name });
      toast.success("Admin angelegt – Link zum Passwort-Festlegen wurde per E-Mail geschickt");
      setForm({ email: "", name: "", password: "" });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Anlegen");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/admin/auth/${a.id}`, { active: !(a.active !== false) });
      toast.success(a.active !== false ? "Deaktiviert" : "Aktiviert");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    }
  };

  const savePassword = async (a) => {
    try {
      await api.post("/admin/auth/forgot", { email: a.email });
      toast.success(`Link an ${a.email} angefordert (max. 1 Link alle 2 Minuten)`);
      setPwEditingId(null);
      setNewPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    }
  };

  const saveName = async (a) => {
    const trimmed = (newName || "").trim();
    if (!trimmed) { toast.error("Name darf nicht leer sein"); return; }
    if (trimmed === a.name) { setNameEditingId(null); return; }
    try {
      await api.patch(`/admin/auth/${a.id}`, { name: trimmed });
      toast.success("Name aktualisiert");
      setNameEditingId(null);
      setNewName("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    }
  };

  const removeAdmin = async (a) => {
    if (!window.confirm(`Admin-Rechte für ${a.email} entfernen? Das Konto bleibt als normaler User bestehen.`)) return;
    try {
      await api.delete(`/admin/auth/${a.id}`);
      toast.success("Admin-Rechte entfernt");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-6" data-testid="admin-accounts-panel">

      <div
        className="plant-card p-4 flex flex-col sm:flex-row sm:items-center gap-3 text-sm"
        style={{ backgroundColor: "var(--wp-cream)" }}
        data-testid="admin-users-hint"
      >
        <div
          className="w-10 h-10 rounded-full inline-flex items-center justify-center shrink-0"
          style={{ backgroundColor: "var(--wp-olive)", color: "var(--wp-cream)" }}
        >
          <Users className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="font-medium" style={{ color: "var(--wp-olive)" }}>Reguläre Nutzer-Konten</div>
          <div className="text-xs opacity-80">Diese Seite verwaltet nur <strong>Admin</strong>-Konten. Alle registrierten Nutzer (Community, Tour-Buchungen usw.) findest du im Tab „User" mit Namen, E-Mail und Erstelldatum.</div>
        </div>
        {goToUsers && (
          <button
            type="button"
            onClick={goToUsers}
            className="btn-secondary text-sm"
            data-testid="admin-goto-users-btn"
          >
            <Users className="w-4 h-4" /> Zu „User" wechseln
          </button>
        )}
      </div>

      <div className="plant-card p-5" data-testid="admin-account-create">
        <div className="overline mb-3">Neuen Admin anlegen</div>
        <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
          <input
            type="email"
            required
            placeholder="E-Mail"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="md:col-span-1 px-3 py-2 rounded-lg border"
            style={{ borderColor: "var(--wp-border)" }}
            data-testid="admin-new-email"
            autoComplete="off"
          />
          <input
            type="text"
            required
            placeholder="Anzeigename"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="md:col-span-1 px-3 py-2 rounded-lg border"
            style={{ borderColor: "var(--wp-border)" }}
            data-testid="admin-new-name"
          />
          <span className="md:col-span-1 text-xs opacity-70 self-center" data-testid="admin-new-password-hint">Das Passwort legt die Person selbst über einen E-Mail-Link fest.</span>
          <button className="btn-primary justify-center" disabled={creating} data-testid="admin-new-submit">
            <UserPlus className="w-4 h-4" /> {creating ? "Lege an…" : "Anlegen"}
          </button>
        </form>
      </div>

      <div className="plant-card overflow-hidden">
        <div className="p-5 border-b" style={{ borderColor: "var(--wp-border)" }}>
          <div className="overline">Alle Admin-Konten ({admins.length})</div>
        </div>
        <ul className="divide-y" style={{ borderColor: "var(--wp-border)" }} data-testid="admin-accounts-list">
          {admins.length === 0 && (
            <li className="p-6 text-sm opacity-70 text-center">Noch keine Admin-Konten. Lege oben eines an.</li>
          )}
          {admins.map((a) => {
            const active = a.active !== false;
            const isMe = me?.email && me.email === a.email;
            return (
              <li key={a.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`admin-row-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" style={{ color: "var(--wp-olive)" }}>
                    {nameEditingId === a.id ? (
                      <div className="flex gap-2 items-center">
                        <input
                          type="text"
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          className="flex-1 px-2 py-1 rounded-lg border text-sm"
                          style={{ borderColor: "var(--wp-border)" }}
                          data-testid={`admin-name-input-${a.id}`}
                          autoFocus
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveName(a); } }}
                        />
                        <button
                          onClick={() => saveName(a)}
                          className="text-xs px-3 py-1 rounded-full"
                          style={{ backgroundColor: "var(--wp-olive)", color: "var(--wp-cream)" }}
                          data-testid={`admin-name-save-${a.id}`}
                        >Speichern</button>
                        <button
                          onClick={() => { setNameEditingId(null); setNewName(""); }}
                          className="text-xs px-3 py-1 rounded-full"
                          style={{ backgroundColor: "var(--wp-cream)", color: "var(--wp-forest)" }}
                        >Abbr.</button>
                      </div>
                    ) : (
                      <>
                        {a.name || "—"}
                        {isMe && <span className="ml-2 inline-flex px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider" style={{ backgroundColor: "var(--wp-cream)", color: "var(--wp-olive)" }}>Du</span>}
                        {!active && <span className="ml-2 inline-flex px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider" style={{ backgroundColor: "rgba(185,74,72,0.15)", color: "var(--wp-toxic)" }}>Inaktiv</span>}
                      </>
                    )}
                  </div>
                  <div className="text-xs opacity-70 truncate">{a.email}</div>
                  {pwEditingId === a.id && (
                    <div className="mt-2 flex gap-2 items-center">
                      <span className="flex-1 text-xs">Link zum Passwort-Festlegen an {a.email} senden?</span>
                      <button
                        onClick={() => savePassword(a)}
                        className="text-xs px-3 py-1.5 rounded-full"
                        style={{ backgroundColor: "var(--wp-olive)", color: "var(--wp-cream)" }}
                        data-testid={`admin-pw-save-${a.id}`}
                      >Link senden</button>
                      <button
                        onClick={() => { setPwEditingId(null); setNewPassword(""); }}
                        className="text-xs px-3 py-1.5 rounded-full"
                        style={{ backgroundColor: "var(--wp-cream)", color: "var(--wp-forest)" }}
                      >Abbr.</button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button
                    onClick={() => { setNameEditingId(a.id); setNewName(a.name || ""); }}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs"
                    style={{ backgroundColor: "var(--wp-cream)", color: "var(--wp-forest)" }}
                    data-testid={`admin-name-edit-${a.id}`}
                    title="Name ändern"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Name
                  </button>
                  <button
                    onClick={() => { setPwEditingId(a.id); setNewPassword(""); }}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs"
                    style={{ backgroundColor: "var(--wp-cream)", color: "var(--wp-forest)" }}
                    data-testid={`admin-pw-edit-${a.id}`}
                    title="Passwort ändern"
                  >
                    <KeyRound className="w-3.5 h-3.5" /> Passwort
                  </button>
                  {!isMe && <>
                  <button
                    onClick={() => toggleActive(a)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs"
                    style={{ backgroundColor: "var(--wp-cream)", color: active ? "var(--wp-toxic)" : "var(--wp-primary)" }}
                    data-testid={`admin-toggle-${a.id}`}
                    title={active ? "Deaktivieren" : "Aktivieren"}
                  >
                    {active ? <><PowerOff className="w-3.5 h-3.5" /> Deaktivieren</> : <><Power className="w-3.5 h-3.5" /> Aktivieren</>}
                  </button>
                  <button
                    onClick={() => removeAdmin(a)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs"
                    style={{ backgroundColor: "rgba(185,74,72,0.12)", color: "var(--wp-toxic)" }}
                    data-testid={`admin-remove-${a.id}`}
                    title="Admin-Rechte entfernen"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Entfernen
                  </button>
                  </>}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="text-xs opacity-60">
        Sicherheitshinweis: Der letzte aktive Admin kann nicht entfernt oder deaktiviert werden, und niemand kann sich selbst deaktivieren.
      </div>
    </div>
  );
}
